import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Building2, User, Calendar as CalendarIcon, Clock, Plus, RefreshCcw } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { useLanguage } from "../../../contexts/LanguageContext";
import { settingsLabels } from "../labels";

interface NewExceptionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  employeeId: string;
  setEmployeeId: (val: string) => void;
  professionals: any[];
  date: string;
  setDate: (val: string) => void;
  repeatType: "none" | "weekly" | "monthly";
  setRepeatType: (val: "none" | "weekly" | "monthly") => void;
  selectedWeekday: number | null;
  selectedDayOfMonth: number | null;
  weekdayNames: string[];
  allDay: boolean;
  setAllDay: (val: boolean) => void;
  startTime: string;
  setStartTime: (val: string) => void;
  endTime: string;
  setEndTime: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  saving: boolean;
}

export function NewExceptionModal({
  isOpen,
  onOpenChange,
  handleSubmit,
  employeeId,
  setEmployeeId,
  professionals,
  date,
  setDate,
  repeatType,
  setRepeatType,
  selectedWeekday,
  selectedDayOfMonth,
  weekdayNames,
  allDay,
  setAllDay,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  description,
  setDescription,
  saving,
}: NewExceptionModalProps) {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].exceptions;

  const onFormSubmit = (e: React.FormEvent) => {
    handleSubmit(e);
    // Modal will be closed by the parent on success if needed, 
    // but usually onOpenChange(false) is called inside handleSubmit or here.
    // For now, let's let the parent handle it or close it on success.
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-[2.5rem] border-none p-0 overflow-hidden bg-white shadow-2xl">
        <DialogHeader className="px-8 py-6 border-b border-neutral-50 bg-neutral-50/30">
          <DialogTitle className="text-sm font-bold uppercase tracking-widest text-neutral-800 flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
              <Plus className="h-4 w-4" />
            </div>
            {t.new}
          </DialogTitle>
        </DialogHeader>

        <div className="p-8">
          <form onSubmit={onFormSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1">{t.fields.type}</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger className="h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus:ring-emerald-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[2rem] shadow-2xl border-neutral-100/50 backdrop-blur-md bg-white/90">
                  <SelectItem value="all" className="rounded-xl py-3 focus:bg-emerald-50 focus:text-emerald-700">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4" />
                      <span className="font-bold">{t.fields.allProfessionals}</span>
                    </div>
                  </SelectItem>
                  {professionals.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)} className="rounded-xl py-3 focus:bg-emerald-50 focus:text-emerald-700">
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4" />
                        <span className="font-bold">{p.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="exc-date"
                className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1 flex items-center gap-1"
              >
                <CalendarIcon className="h-3 w-3" />
                Data {repeatType !== "none" ? "(Início)" : ""}
              </Label>
              <Input
                id="exc-date"
                type="date"
                className="h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-bold text-neutral-800"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-6">
              <div 
                className="flex items-center justify-between p-4 rounded-2xl bg-neutral-50/50 border border-neutral-100 transition-all hover:bg-white"
                onClick={() => setAllDay(!allDay)}
              >
                <div className="space-y-0.5">
                  <Label
                    htmlFor="all-day"
                    className="text-xs font-bold text-neutral-800 uppercase tracking-widest cursor-pointer select-none"
                  >
                    {t.fields.allDay}
                  </Label>
                  <p className="text-[10px] text-neutral-400 font-medium">
                    {lang === 'pt' ? 'Bloquear o dia completo' : 'Block the full day'}
                  </p>
                </div>
                <Switch 
                  id="all-day" 
                  checked={allDay} 
                  onCheckedChange={setAllDay} 
                  className="data-[state=checked]:bg-emerald-600"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1">Recorrência</Label>
                <div className="flex flex-col gap-2">
                  {[
                    { id: "none", label: lang === "pt" ? "Apenas esta data" : "Only this date" },
                    { id: "weekly", label: lang === "pt" ? `Toda ${selectedWeekday !== null ? weekdayNames[selectedWeekday] : "semana"}` : `Every ${selectedWeekday !== null ? weekdayNames[selectedWeekday] : "week"}` },
                    { id: "monthly", label: lang === "pt" ? `Todo dia ${selectedDayOfMonth} de cada mês` : `Every ${selectedDayOfMonth}th of each month` }
                  ].map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setRepeatType(type.id as any)}
                      className={cn(
                        "flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left group",
                        repeatType === type.id 
                          ? "border-emerald-600 bg-emerald-50/30 text-emerald-900" 
                          : "border-neutral-50 bg-neutral-50/30 hover:border-neutral-200 text-neutral-500"
                      )}
                    >
                      <div className={cn(
                        "w-2 h-2 rounded-full transition-all",
                        repeatType === type.id ? "bg-emerald-600 scale-125" : "bg-neutral-300 group-hover:bg-neutral-400"
                      )} />
                      <span className="text-[11px] font-bold uppercase tracking-widest">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {!allDay && (
              <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
                <div className="space-y-2">
                  <Label
                    htmlFor="exc-start"
                    className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1 flex items-center gap-1"
                  >
                    {t.fields.start}
                  </Label>
                  <Input
                    id="exc-start"
                    type="time"
                    className="h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-bold text-neutral-800"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required={!allDay}
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="exc-end"
                    className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1 flex items-center gap-1"
                  >
                    {t.fields.end}
                  </Label>
                  <Input
                    id="exc-end"
                    type="time"
                    className="h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-bold text-neutral-800"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required={!allDay}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="exc-desc" className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 ml-1">
                {t.fields.description}
              </Label>
              <Input
                id="exc-desc"
                className="h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-bold text-neutral-800"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Feriado municipal"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest rounded-2xl transition-all active:scale-95 mt-4"
              disabled={saving}
            >
              {saving ? (
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.new}
                </>
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
